/**
 * PlyxCreatorInsights — Pro creator coaching dashboard.
 *
 * Hits GET /api/plyx/ai/creator-insights to surface:
 *   • 30-day stats roll-up (posts, likes, comments, shares, views)
 *   • One-sentence summary
 *   • Exactly 3 AI-generated, actionable tips
 *
 * Pro-gated server-side → 402 surfaces a Go-Pro upsell here. Lazy-loads
 * on first mount; "Refresh insights" button re-runs the LLM call.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, TrendingUp, RefreshCw, ArrowLeft, Share2, Download, Image as ImageIcon } from "lucide-react";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

const StatBlock = ({ label, value }) => (
  <div className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-lg py-3 px-2">
    <span className="text-lg font-black text-white">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{label}</span>
  </div>
);

const PlyxCreatorInsights = () => {
  const { user } = usePlyxAuth();
  const [loading, setLoading] = useState(true);
  const [paywalled, setPaywalled] = useState(false);
  const [data, setData] = useState(null);

  const fetchInsights = async (showToast = false) => {
    setLoading(true);
    try {
      const r = await plyxApi.get("/plyx/ai/creator-insights");
      setData(r.data);
      setPaywalled(false);
      if (showToast) toast.success("Insights refreshed");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const code = (detail && typeof detail === "object") ? detail.code : null;
      if (status === 402 || code === "pro_required") {
        setPaywalled(true);
      } else {
        toast.error((typeof detail === "string" && detail) || "Couldn't load insights");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchInsights(false);
  }, [user?.plyx_user_id]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="font-semibold mb-2">Sign in to view your insights</div>
          <Link
            to="/plyx/login"
            data-testid="plyx-insights-signin"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-white font-semibold text-sm"
          >
            Log in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-creator-insights-page">
      <header className="sticky top-0 z-10 backdrop-blur bg-[#0A0A0A]/85 border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <Link
          to="/plyx"
          data-testid="plyx-insights-back"
          className="w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center"
          aria-label="Back to feed"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-white/45">Creator</div>
          <div className="font-bold text-base flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#FFB42D]" />
            Insights
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchInsights(true)}
          disabled={loading || paywalled}
          data-testid="plyx-insights-refresh"
          className="w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center disabled:opacity-30"
          title="Refresh insights"
          aria-label="Refresh insights"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-40">
        {paywalled ? (
          <div
            className="rounded-xl p-6 text-center bg-gradient-to-br from-[#FF2D7A]/15 via-[#FFB42D]/10 to-[#00E0C7]/10 border border-[#FFB42D]/30"
            data-testid="plyx-insights-paywall"
          >
            <Sparkles className="w-8 h-8 text-[#FFB42D] mx-auto mb-3" />
            <div className="text-lg font-black mb-1">Creator Insights is a Plyx Pro perk</div>
            <div className="text-sm text-white/70 mb-5">
              Get a weekly AI breakdown of your stats with 3 specific tips to grow faster.
            </div>
            <Link
              to="/plyx/pro"
              data-testid="plyx-insights-go-pro"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-extrabold text-white text-sm"
              style={{ background: "#E85D4E" }}
            >
              Go Pro
            </Link>
          </div>
        ) : loading && !data ? (
          <div className="flex items-center justify-center py-20 text-white/60">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Crunching your numbers…
          </div>
        ) : data ? (
          <>
            <section className="mb-6" data-testid="plyx-insights-stats">
              <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-2">
                Last 30 days
              </div>
              <div className="grid grid-cols-5 gap-2">
                <StatBlock label="Posts" value={data.stats?.posts ?? 0} />
                <StatBlock label="Likes" value={data.stats?.likes ?? 0} />
                <StatBlock label="Comments" value={data.stats?.comments ?? 0} />
                <StatBlock label="Shares" value={data.stats?.shares ?? 0} />
                <StatBlock label="Views" value={data.stats?.views ?? 0} />
              </div>
            </section>

            <section className="mb-6" data-testid="plyx-insights-summary">
              <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-2">
                Summary
              </div>
              <div className="text-sm text-white/85 leading-relaxed bg-white/5 border border-white/10 rounded-lg p-4">
                {data.summary}
              </div>
            </section>

            <section data-testid="plyx-insights-tips">
              <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-white/85" />
                What to try next
              </div>
              <ul className="space-y-2">
                {(data.tips || []).map((tip, i) => (
                  <li
                    key={i}
                    data-testid={`plyx-insights-tip-${i}`}
                    className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-lg p-3"
                  >
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white font-black text-xs shrink-0"
                      style={{ background: "linear-gradient(135deg, #FF2D7A, #FFB42D, #00E0C7)" }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-sm text-white/90 leading-snug">{tip}</span>
                  </li>
                ))}
              </ul>
            </section>

            <StoryGraphicSection />
          </>
        ) : (
          <div className="text-white/60 text-sm py-10 text-center">No insights yet.</div>
        )}
      </main>
    </div>
  );
};

export default PlyxCreatorInsights;

/**
 * StoryGraphicSection — fetches the PNG from the backend and lets the
 * creator preview, share, or download it. Uses Web Share API when
 * available (mobile Safari + Chrome) so a one-tap share to Instagram
 * Stories / WhatsApp / X / Telegram works natively.
 */
const StoryGraphicSection = () => {
  const [range, setRange] = useState(7);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const blobRef = useRef(null);

  const buildGraphic = async () => {
    setBusy(true);
    try {
      const r = await plyxApi.get(`/plyx/insights/story-graphic.png?range_days=${range}&top_n=3`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "image/png" });
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      toast.success(`Your ${range}-day graphic is ready`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Couldn't render the graphic");
    } finally { setBusy(false); }
  };

  const handleShare = async () => {
    if (!blobRef.current) { await buildGraphic(); return; }
    const file = new File([blobRef.current], `plyx-week.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: "My Plyx week",
          text: "My Plyx stats — share it on your story 📈",
        });
        return;
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.warn("share failed:", err);
        }
      }
    }
    // Fallback: download.
    handleDownload();
  };

  const handleDownload = () => {
    if (!blobRef.current) { toast.error("Build the graphic first"); return; }
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plyx-week-${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  return (
    <section className="mt-8" data-testid="plyx-insights-story-graphic">
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold mb-2 flex items-center gap-1.5">
        <ImageIcon className="w-3.5 h-3.5 text-[#FFCC2E]"/>
        Auto Story graphic <span className="ml-auto text-[#FFCC2E]">free</span>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-fuchsia-600/30 via-amber-500/20 to-cyan-500/30 p-[1px]">
        <div className="rounded-2xl bg-[#0a0a0f] p-4">
          <div className="text-sm text-white/85 mb-3 leading-snug">
            One-tap share your week on Instagram Stories, WhatsApp, or anywhere. Rendered at 1080×1920.
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="plyx-insights-story-range">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => { setRange(d); blobRef.current = null; if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}
                className={`text-xs font-bold px-3 py-1.5 rounded-full transition ${range === d ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                data-testid={`plyx-insights-story-range-${d}`}
              >Last {d} days</button>
            ))}
          </div>
          {previewUrl ? (
            <div className="mb-3">
              <img src={previewUrl} alt="Plyx weekly stats preview" className="w-full max-w-[280px] mx-auto rounded-xl shadow-2xl ring-1 ring-white/15" data-testid="plyx-insights-story-preview"/>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={previewUrl ? handleShare : buildGraphic}
              disabled={busy}
              className="bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60"
              data-testid="plyx-insights-story-share"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : (previewUrl ? <Share2 className="w-4 h-4"/> : <Sparkles className="w-4 h-4"/>)}
              {previewUrl ? "Share to Story" : "Build graphic"}
            </button>
            <button
              onClick={handleDownload}
              disabled={!previewUrl || busy}
              className="bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-40"
              data-testid="plyx-insights-story-download"
            >
              <Download className="w-3.5 h-3.5"/> Download PNG
            </button>
          </div>
          {previewUrl && (
            <button
              onClick={buildGraphic}
              className="w-full mt-2 text-[11px] text-white/40 hover:text-white"
              data-testid="plyx-insights-story-rebuild"
            >Rebuild with fresh stats</button>
          )}
        </div>
      </div>
    </section>
  );
};
